import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ban, Landmark, Link2, Loader2, Mail, Search } from 'lucide-react';
import { klarysUrl } from './klarysApi';
import { KlarysPayeurs } from './KlarysPayeurs';

const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fr = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');

const Attribution = ({ v, i, headers, onFini, onAttribue }) => {
  const [q, setQ] = useState('');
  const [cands, setCands] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fait, setFait] = useState(null);
  const chercher = async (texte = q) => {
    setBusy(true);
    try { const r = await axios.get(klarysUrl(`/virements/${v.id}/candidats`), { headers, params: { q: texte } }); setCands(r.data.items); setSel(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Recherche impossible'); } finally { setBusy(false); }
  };
  useEffect(() => { chercher(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const attribuer = async () => {
    setBusy(true);
    try { const r = await axios.post(klarysUrl(`/virements/${v.id}/attribuer`), { produit: sel.produit, ident: sel.ident }, { headers }); setFait(r.data); onAttribue(); toast.success(`Virement attribué à ${sel.nom}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Attribution impossible'); } finally { setBusy(false); }
  };
  const envoyer = async () => {
    setBusy(true);
    try { await axios.post(klarysUrl(`/virements/${v.id}/envoyer-recu`), {}, { headers }); toast.success('Reçu et attestation envoyés'); onFini(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); } finally { setBusy(false); }
  };
  if (fait) {
    const r = fait.recus?.[0];
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-900" data-testid={`klarys-attribuer-fait-${i}`}>
        <p><b>{fait.contrat}</b> : {fait.echeances_payees} échéance(s) marquée(s) payée(s) par virement. {fait.ged}.</p>
        {r?.email ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span>Envoyer à {r.nom} ({r.email}) son reçu de paiement et son attestation à jour ?</span>
            <button type="button" onClick={envoyer} disabled={busy} data-testid={`klarys-attribuer-envoyer-${i}`}
              className="inline-flex items-center gap-1 rounded-md bg-[#0a3d62] px-2.5 py-1 font-semibold text-white disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Envoyer reçu + attestation
            </button>
            <button type="button" onClick={onFini} className="underline" data-testid={`klarys-attribuer-plus-tard-${i}`}>Plus tard</button>
          </div>
        ) : <button type="button" onClick={onFini} className="mt-1 underline">Fermer</button>}
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs" data-testid={`klarys-attribuer-panneau-${i}`}>
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && chercher()} placeholder="Nom du membre, référence du contrat, e-mail…"
          className="flex-1 rounded border border-slate-300 px-2 py-1" data-testid={`klarys-attribuer-recherche-${i}`} />
        <button type="button" onClick={() => chercher()} disabled={busy} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1" data-testid={`klarys-attribuer-chercher-${i}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Chercher
        </button>
      </div>
      {cands && !cands.length && <p className="text-slate-500">Aucun contrat impayé correspondant. Essayez un autre nom.</p>}
      {cands?.map((c, j) => (
        <label key={`${c.produit}-${c.ident}`} className={`flex cursor-pointer items-start gap-2 rounded border bg-white px-2 py-1.5 ${sel === c ? 'border-[#0a3d62]' : 'border-slate-200'}`} data-testid={`klarys-attribuer-cand-${i}-${j}`}>
          <input type="radio" checked={sel === c} onChange={() => setSel(c)} className="mt-0.5 accent-[#0a3d62]" />
          <span className="flex-1">{c.payeur_connu && <span className="mr-1 rounded bg-emerald-100 px-1 font-semibold text-emerald-800" data-testid={`klarys-attribuer-connu-${i}-${j}`}>Payeur connu</span>}<b>{c.nom}</b> · {c.produit_label} {c.reference} · <span className="text-rose-700">{c.etat_label}</span> · dû {eur(c.total_a_regler)}
            <span className="block text-slate-500">{c.echeances.length} échéance(s) couverte(s){Math.abs(c.ecart) >= 0.01 ? ` · écart ${eur(c.ecart)}` : ' · montant exact'}</span></span>
        </label>
      ))}
      {sel && (
        <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-900">
          <span className="flex-1">Confirmer : enregistrer ce virement de {eur(v.montant)} sur {sel.produit_label} {sel.reference} ({sel.nom}) ?</span>
          <button type="button" onClick={attribuer} disabled={busy} className="rounded bg-emerald-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50" data-testid={`klarys-attribuer-confirmer-${i}`}>Confirmer</button>
        </div>
      )}
    </div>
  );
};

const Ignorer = ({ v, i, headers, onFini }) => {
  const [motif, setMotif] = useState('');
  const valider = async () => {
    try { await axios.post(klarysUrl(`/virements/${v.id}/ignorer`), { motif }, { headers }); toast.success('Virement marqué hors adhésion'); onFini(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
  };
  return (
    <div className="mt-2 flex gap-2 text-xs">
      <input autoFocus value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (ex. remboursement fournisseur, don…)" className="flex-1 rounded border border-slate-300 px-2 py-1" data-testid={`klarys-ignorer-motif-${i}`} />
      <button type="button" onClick={valider} disabled={motif.trim().length < 3} className="rounded bg-slate-700 px-2.5 py-1 font-semibold text-white disabled:opacity-50" data-testid={`klarys-ignorer-valider-${i}`}>Valider</button>
    </div>
  );
};

export const KlarysAttribuer = ({ headers, onCompte }) => {
  const [vue, setVue] = useState('a_attribuer');
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const charger = () => axios.get(klarysUrl('/virements'), { headers, params: { statut: vue } }).then((r) => { setData(r.data); onCompte?.(r.data.nb_a_attribuer); }).catch(() => toast.error('Liste indisponible'));
  useEffect(() => { setData(null); if (vue !== 'payeurs') charger(); }, [vue]); // eslint-disable-line react-hooks/exhaustive-deps
  const fini = () => { setOuvert(null); charger(); };
  return (
    <div className="h-full space-y-3 overflow-y-auto bg-[#f6f8fb] p-4" data-testid="klarys-attribuer">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Landmark className="h-4 w-4 text-[#0a3d62]" /> Virements reçus non identifiés</p>
        {data && <span className="text-xs text-slate-500" data-testid="klarys-attribuer-total">{data.nb_a_attribuer} à attribuer — {eur(data.total_a_attribuer)}</span>}
        <div className="ml-auto flex gap-1 text-xs">
          {[['a_attribuer', 'À attribuer'], ['traites', 'Traités'], ['payeurs', 'Payeurs connus']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setVue(k)} data-testid={`klarys-attribuer-vue-${k}`}
              className={`rounded-md px-2.5 py-1 ${vue === k ? 'bg-[#0a3d62] text-white' : 'border border-slate-300 text-slate-600'}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-500">Les virements d'un relevé non enregistrés lors du rapprochement arrivent ici. Un rappel figure dans le rapport du matin tant qu'il en reste.</p>
      {vue === 'payeurs' ? <KlarysPayeurs headers={headers} /> : !data ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#0a3d62]" /> : !data.items.length ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="klarys-attribuer-vide">{vue === 'a_attribuer' ? 'Aucun virement à attribuer.' : 'Aucun virement traité.'}</p>
      ) : (
        <ul className="space-y-2">
          {data.items.map((v, i) => (
            <li key={v.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`klarys-virement-${i}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold text-slate-900" data-testid={`klarys-virement-montant-${i}`}>{eur(v.montant)}</span>
                <span className="text-xs text-slate-500">{fr(v.date)}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{v.payeur ? <b>{v.payeur} · </b> : null}{v.libelle}</span>
                {vue === 'a_attribuer' ? (
                  <span className="flex gap-1.5 text-xs">
                    <button type="button" onClick={() => setOuvert(ouvert?.id === v.id && ouvert.k === 'a' ? null : { id: v.id, k: 'a' })} data-testid={`klarys-virement-attribuer-${i}`}
                      className="inline-flex items-center gap-1 rounded-md bg-[#0a3d62] px-2.5 py-1 font-semibold text-white"><Link2 className="h-3.5 w-3.5" /> Attribuer</button>
                    <button type="button" onClick={() => setOuvert(ouvert?.id === v.id && ouvert.k === 'i' ? null : { id: v.id, k: 'i' })} data-testid={`klarys-virement-ignorer-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-slate-600"><Ban className="h-3.5 w-3.5" /> Hors adhésion</button>
                  </span>
                ) : (
                  <span className={`rounded px-1.5 text-[11px] font-semibold ${v.statut === 'attribue' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`} data-testid={`klarys-virement-statut-${i}`}>
                    {v.statut === 'attribue' ? `Attribué : ${v.contrat}${v.recu_envoye ? ' · reçu envoyé' : ''}` : `Ignoré : ${v.motif}`}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-slate-400">Relevé : {v.fichier_nom}{v.traite_par ? ` · traité par ${v.traite_par} le ${fr(v.traite_le)}` : ''}</p>
              {ouvert?.id === v.id && ouvert.k === 'a' && <Attribution v={v} i={i} headers={headers} onFini={fini} onAttribue={() => { setData((d) => ({ ...d, nb_a_attribuer: d.nb_a_attribuer - 1, total_a_attribuer: d.total_a_attribuer - v.montant })); onCompte?.(Math.max(0, data.nb_a_attribuer - 1)); }} />}
              {ouvert?.id === v.id && ouvert.k === 'i' && <Ignorer v={v} i={i} headers={headers} onFini={fini} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
